import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';

/**
 * @dynamic
 */
@Injectable({ providedIn: 'root' })
export class CopierService {
  private _fakeElem: HTMLTextAreaElement | null;

  private _document = inject(DOCUMENT);

  public copyText(text: string): boolean {
    try {
      this._createFake(text);

      return this._document.execCommand('copy');
    } catch {
      return false;
    } finally {
      this._removeFake();
    }
  }

  /**
   * Creates a fake textarea element, sets its value from `text` property,
   * and makes a selection on it.
   */
  public _createFake(text: string): void {
    const docElem = this._document.documentElement;
    const isRTL = docElem.getAttribute('dir') === 'rtl';

    // Create a fake element to hold the contents to copy
    this._fakeElem = this._document.createElement('textarea');

    // Prevent zooming on iOS
    this._fakeElem.style.fontSize = '12pt';

    // Reset box model
    this._fakeElem.style.border = '0';
    this._fakeElem.style.padding = '0';
    this._fakeElem.style.margin = '0';

    // Move element out of screen horizontally
    this._fakeElem.style.position = 'absolute';
    this._fakeElem.style[isRTL ? 'right' : 'left'] = '-9999px';

    // Move element to the same position vertically
    const yPosition = window.pageYOffset || docElem.scrollTop;
    this._fakeElem.style.top = yPosition + 'px';

    this._fakeElem.setAttribute('readonly', '');
    this._fakeElem.value = text;

    this._document.body.appendChild(this._fakeElem);

    this._fakeElem.select();
    this._fakeElem.setSelectionRange(0, this._fakeElem.value.length);
  }

  public _removeFake(): void {
    if (this._fakeElem) {
      this._document.body.removeChild(this._fakeElem);
      this._fakeElem = null;
    }
  }
}
